/**
 * Gated Content Section
 *
 * Implements progressive disclosure pattern:
 * - Shows public teaser to intrigue visitors
 * - Displays blurred preview of full content
 * - Requires access request approval for full access
 *
 * Supports three access levels:
 * 1. Public: Always visible teaser/summary
 * 2. Gated: Full content visible only with valid token
 * 3. Locked: Blurred preview with "Request Access" button
 */

import { useState } from 'react'
import { AccessRequestModal } from './AccessRequestModal'
import { useCheckAccess } from '@/hooks/api/useAccessControl'

interface GatedContentSectionProps {
  /** Public teaser content - always visible */
  publicContent: React.ReactNode

  /** Full content - only visible with valid access */
  gatedContent: React.ReactNode

  /** Content type identifier (e.g., 'background', 'interests') */
  contentType: string

  /** Display label for access request (e.g., "Full Background") */
  contentLabel: string

  /** Optional access token from URL params */
  accessToken?: string

  /** Custom styling for the section */
  className?: string
}

export function GatedContentSection({
  publicContent,
  gatedContent,
  contentType,
  contentLabel,
  accessToken,
  className = '',
}: GatedContentSectionProps) {
  const [showRequestModal, setShowRequestModal] = useState(false)

  // Check if user has valid access token
  const { data: accessCheck } = useCheckAccess(accessToken, contentType)
  const hasAccess = accessCheck?.hasAccess || false

  // If user has access, show full content
  if (hasAccess) {
    return (
      <div className={className}>
        {/* Public teaser still shown for context */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>{publicContent}</div>

        {/* Full gated content */}
        <div
          style={{
            padding: 'var(--spacing-xl)',
            backgroundColor: 'rgba(34, 197, 94, 0.05)',
            borderLeft: '4px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-md)',
              fontSize: '0.875rem',
              color: 'var(--color-success)',
              fontWeight: 600,
            }}
          >
            <span>✅</span>
            <span>You have access to detailed content</span>
          </div>
          {gatedContent}
        </div>
      </div>
    )
  }

  // No access - show teaser + locked preview
  return (
    <div className={className}>
      {/* Public teaser content */}
      <div style={{ marginBottom: 'var(--spacing-xl)' }}>{publicContent}</div>

      {/* Locked content preview */}
      <div
        style={{
          position: 'relative',
          padding: 'var(--spacing-xl)',
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '2px dashed rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Blurred preview of gated content */}
        <div
          style={{
            filter: 'blur(8px)',
            opacity: 0.4,
            userSelect: 'none',
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        >
          {gatedContent}
        </div>

        {/* Lock overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {/* Lock icon */}
          <div
            style={{
              fontSize: '3rem',
              marginBottom: 'var(--spacing-md)',
              opacity: 0.6,
            }}
            aria-hidden="true"
          >
            🔒
          </div>

          {/* Call to action */}
          <div
            style={{
              textAlign: 'center',
              maxWidth: '400px',
              padding: '0 var(--spacing-md)',
            }}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-sm)',
              }}
            >
              Detailed Content Available
            </h3>
            <p
              style={{
                fontSize: '0.9375rem',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-lg)',
                lineHeight: 1.6,
              }}
            >
              This section contains comprehensive details available to qualified leads.
              Request access to view the full content.
            </p>

            {/* Request Access button */}
            <button
              onClick={() => setShowRequestModal(true)}
              style={{
                padding: 'var(--spacing-md) var(--spacing-xl)',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
            >
              🔓 Request Access to {contentLabel}
            </button>

            {/* Trust signal */}
            <p
              style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-md)',
                fontStyle: 'italic',
              }}
            >
              Your request will be reviewed and you'll receive email confirmation if approved
            </p>
          </div>
        </div>
      </div>

      {/* Access Request Modal */}
      <AccessRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        requestedContent={[contentType]}
        contentLabel={contentLabel}
      />
    </div>
  )
}
